import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ApplicationState } from 'store';
import { getComparator, stableSort } from 'helpers/tableSortLabels';
import { extractHoursOnDateString } from 'helpers';
import { calcReschedulePriceDifference } from 'helpers/ticketSales';

import {
  ResumeItineraryVesselsInterface,
  ItineraryResumeTripCapacityList,
  SeatsInterface,
} from 'store/ducks/itineraries/types';
import { BOARDING_TAX_TYPE, OPERATION } from 'constants/ticketSales';

export interface ItineraryListWithCapacity {
  [key: string]: any;
  seats: SeatsInterface[];
  tripScheduleId: string;
  footCapacity: number;
  vehicleCapacity: number;
  exist: boolean;
  thereAreVacancies: boolean;
}

export interface TripFilterResult {
  itineraryList: ItineraryListWithCapacity[];
  /** Há viagens carregadas, mas a capacidade delas ainda não chegou. */
  isCapacityPending: boolean;
  setItineraryList: React.Dispatch<
    React.SetStateAction<ItineraryListWithCapacity[]>
  >;
  timeExit: { morning: boolean; afternoon: boolean; night: boolean };
  setTimeExit: React.Dispatch<
    React.SetStateAction<{
      morning: boolean;
      afternoon: boolean;
      night: boolean;
    }>
  >;
  vacancies: { vehicle: boolean; passagers: boolean };
  setVacancies: React.Dispatch<
    React.SetStateAction<{ vehicle: boolean; passagers: boolean }>
  >;
  orderVessel: string;
  setOrderVessel: React.Dispatch<React.SetStateAction<string>>;
}

function calcCapacity(
  found: ItineraryResumeTripCapacityList,
  trip: { lineSectionShortId?: string },
): { footCapacity: number; vehicleCapacity: number } {
  let footCapacity = 0;
  let vehicleCapacity = 0;

  if (found.sections) {
    const section = Object.entries(found.sections).find(
      ([key]) => key === trip.lineSectionShortId,
    );
    if (section) {
      const footBooked = section[1].footBookedEntries
        .split(',')
        .filter(Boolean);
      const sitBooked =
        section[1].sitBookedEntries?.split(',').filter(Boolean) || [];
      footCapacity =
        Number(section[1].footCapacity) +
        Number(section[1].sitCapacity) -
        (footBooked.length + sitBooked.length);
    }
  } else {
    footCapacity =
      Number(found?.footCapacity) +
      Number(found?.sitCapacity) -
      (found?.entriesAtMoment?.length +
        found?.footBookedEntries?.length +
        found?.sitBookedEntries?.length);
    vehicleCapacity =
      Number(found?.vehicleCapacity) - Number(found?.vehicleEntries);
  }

  return { footCapacity, vehicleCapacity };
}

export function useTripFilter(): TripFilterResult {
  const initialItineraryResumeList = useSelector(
    (state: ApplicationState) =>
      state.itineraries.itineraryResumeList as ResumeItineraryVesselsInterface,
  );
  const itineraryResumeTripCapacityList = useSelector(
    (state: ApplicationState) =>
      state.itineraries.itineraryResumeTripCapacityList,
  );
  const step = useSelector((state: ApplicationState) => state.publicStore.step);
  const rescheduleData = useSelector(
    (state: ApplicationState) => state.publicStore.rescheduleData,
  );
  const ticketData = useSelector(
    (state: ApplicationState) => state.publicStore.ticketData,
  );

  const [timeExit, setTimeExit] = useState({
    morning: false,
    afternoon: false,
    night: false,
  });
  const [vacancies, setVacancies] = useState({
    vehicle: false,
    passagers: false,
  });
  const [orderVessel, setOrderVessel] = useState('start');
  const [itineraryList, setItineraryList] = useState<
    ItineraryListWithCapacity[]
  >([]);
  // Chave (tripScheduleIds) das viagens para as quais itineraryList foi
  // calculada. Garante que a lista exibida seja sempre das viagens atuais.
  const computedForTripsRef = useRef('');
  const [isCapacityPending, setIsCapacityPending] = useState(false);

  useEffect(() => {
    const trips = initialItineraryResumeList?.trips;

    // Nova busca em andamento (o reducer zera itineraryResumeList no REQUEST)
    // ou busca sem resultados: descarta a lista derivada da busca anterior.
    if (!trips?.length) {
      computedForTripsRef.current = '';
      setItineraryList([]);
      setIsCapacityPending(false);
      return;
    }

    const tripsKey = trips.map((trip) => trip.tripScheduleId).join(',');

    let shortResult = stableSort(
      stableSort(trips, getComparator('asc', 'lineName')),
      getComparator('asc', orderVessel),
    )
      .map((trip) => {
        const found = itineraryResumeTripCapacityList.find(
          (item) => item.id === trip.tripScheduleId,
        );
        const { footCapacity, vehicleCapacity } = found
          ? calcCapacity(found, trip)
          : { footCapacity: 0, vehicleCapacity: 0 };

        return {
          ...trip,
          footCapacity,
          vehicleCapacity,
          exist: !!found,
        };
      })
      .map((trip) => ({
        ...trip,
        thereAreVacancies: trip.footCapacity > 0 || trip.vehicleCapacity > 0,
      }))
      // Filtra viagens já passadas no dia de hoje
      .filter((trip) => {
        const now = new Date();
        const hoursToday = new Date().getHours();
        const minutesToday = new Date().getMinutes();
        if (!trip?.start) return false;
        const str = extractHoursOnDateString(trip.start)?.split(':');
        const hours = Number(str[0]);
        const minutes = Number(str[1]);
        const goDate = ticketData?.date?.go
          ? new Date(ticketData.date.go)
          : null;
        const backDate = ticketData?.date?.back
          ? new Date(ticketData.date.back)
          : null;
        const rescheduleDate = ticketData.selectedDay
          ? new Date(ticketData.selectedDay)
          : null;

        const tripDateTime = step.confirmRescheduling.toShow
          ? rescheduleDate?.getTime()
          : goDate?.getTime() || backDate?.getTime();
        if (tripDateTime && tripDateTime > now.getTime()) return true;

        if (
          hours < hoursToday ||
          (hours === hoursToday && minutes < minutesToday)
        ) {
          return false;
        }
        return true;
      })
      // Filtra para reagendamento
      .filter((trip) => {
        if (!step.confirmRescheduling.toShow) return true;

        const priceDifference = calcReschedulePriceDifference(
          trip,
          rescheduleData.amount || 0,
        );
        if (priceDifference < 0) return false;

        if (rescheduleData.reschedule) return true;
        if (rescheduleData.vehicleType) {
          const carFaresFound = trip.carFares?.find(
            (fare: any) => fare.vehicleType === rescheduleData.vehicleType,
          );
          const carFaresTaxesFound = trip.carFareTaxes?.find(
            (fare: any) => fare.vehicleType === rescheduleData.vehicleType,
          );
          let carSum =
            (carFaresFound?.value || 0) +
            (carFaresTaxesFound?.value || 0) +
            trip.fare * rescheduleData.passengers;
          if (trip?.boardingTax) carSum += trip.boardingTax;
          return rescheduleData.amount === carSum;
        }
        let sum = 0;
        const found = trip.otherProfilesProducts?.find(
          (item: any) => item.id === rescheduleData.profileId,
        );
        const fixed =
          BOARDING_TAX_TYPE.FIXED_VALUE === trip?.boardingTaxRules?.type;
        if (found) {
          if (trip?.fareRules?.website?.operation === OPERATION.ADD) {
            sum = found.value + found.taxValue;
          } else {
            sum = found.value - found.taxValue;
          }
          if (trip?.boardingTax) {
            sum += fixed
              ? trip.boardingTax
              : (trip?.boardingTax / 100) * found.value;
          }
        } else {
          if (trip?.boardingTax) {
            if (trip?.fareRules?.website?.operation === OPERATION.ADD) {
              sum = trip.fare + trip.fareTax;
            } else {
              sum = trip.fare - trip.fareTax;
            }
            sum = trip.fare;
            sum += fixed
              ? trip.boardingTax
              : (trip?.boardingTax / 100) * trip.fare;
          }
        }
        return rescheduleData.amount.toFixed(2) === sum.toFixed(2);
      });

    const exist = shortResult.every((t) => t.exist);
    if (!exist) {
      // A capacidade carregada não corresponde a estas viagens: ainda não
      // chegou ou pertence à busca anterior. Se a lista atual for de outras
      // viagens, limpa para não misturar; se for das mesmas (atualização
      // periódica da capacidade), mantém a lista até a nova capacidade chegar.
      if (computedForTripsRef.current !== tripsKey) {
        setItineraryList([]);
        setIsCapacityPending(true);
      }
      return;
    }
    computedForTripsRef.current = tripsKey;
    setIsCapacityPending(false);

    // Filtro por horário
    const { morning, afternoon, night } = timeExit;
    if (morning || afternoon || night) {
      shortResult = shortResult.filter((item) => {
        const str = extractHoursOnDateString(item.start).split(':');
        const hours = Number(str[0]);
        const hasMorning = morning && hours >= 6 && hours <= 11;
        const hasAfternoon = afternoon && hours >= 12 && hours <= 17;
        const hasNight = night && hours >= 18 && hours <= 23;
        return hasMorning || hasAfternoon || hasNight;
      });
    }

    // Filtro por vagas
    const { vehicle, passagers } = vacancies;
    if (vehicle || passagers) {
      shortResult = shortResult.filter(
        (item) =>
          (vehicle && item.vehicleCapacity) ||
          (passagers && item.footCapacity),
      );
    }

    const selId = ticketData?.selectedTravelGo?.tripScheduleId;
    if (selId) {
      const sorted = [...shortResult].sort((a, b) => {
        if (a.tripScheduleId === selId) return -1;
        if (b.tripScheduleId === selId) return 1;
        return 0;
      });
      setItineraryList(sorted as ItineraryListWithCapacity[]);
    } else {
      setItineraryList(shortResult as ItineraryListWithCapacity[]);
    }
  }, [
    itineraryResumeTripCapacityList,
    initialItineraryResumeList,
    orderVessel,
    timeExit,
    vacancies,
    step.confirmRescheduling.toShow,
    rescheduleData,
    ticketData,
  ]);

  return {
    itineraryList,
    isCapacityPending,
    setItineraryList,
    timeExit,
    setTimeExit,
    vacancies,
    setVacancies,
    orderVessel,
    setOrderVessel,
  };
}
